/**
 * Capa de conocimiento verificado (derivada).
 *
 * Jerarquía de verdad: SEO-EVIDENCE-REGISTER.md → VERIFIED-CLAIMS.md →
 * src/data/entity.ts → copy pública → este archivo / ai.txt / llms.txt / schema.
 *
 * Este módulo NO introduce hechos propios: reutiliza `entity.ts` y solo
 * agrega valores con estado VERIFIED u OWNER_VERIFIED en el registro.
 * Alimenta /informacion-oficial y los archivos estáticos generados por
 * scripts/generate-knowledge-files.ts.
 */
import {
  CONTACT,
  ENTITY_IDS,
  EXPERIENCE,
  PERSON_SAME_AS,
  POSTAL_ADDRESS,
  SITE_URL,
} from "./entity";

export type FactStatus = "VERIFIED" | "OWNER_VERIFIED" | "PUBLIC_SITE_FACT";

type Bi = { es: string; en: string };
type BiLink = { name: Bi; path: Bi };

/** Fecha real de revisión de esta capa (owner + auditoría, 2026-10-08). */
export const KNOWLEDGE_LAST_REVIEWED = "2026-10-08";

export const OFFICIAL_INFO_PATH: Bi = {
  es: "/informacion-oficial",
  en: "/en/official-information",
};

export const VERIFIED_KNOWLEDGE = {
  ids: ENTITY_IDS,
  canonicalHost: SITE_URL,

  identity: {
    name: "Luis ROC",
    role: { es: "Fundador y consultor de SEO y Search Growth", en: "Founder & SEO / Search Growth Consultant" } as Bi,
    experience: { es: `${EXPERIENCE.es}. ${EXPERIENCE.sinceEs}`, en: `${EXPERIENCE.en}. ${EXPERIENCE.sinceEn}` } as Bi,
    status: "OWNER_VERIFIED" as FactStatus,
    source: { es: "/sobre-mi", en: "/en/about" } as Bi,
  },

  organization: {
    legalName: "ROC Worldwide Agency LLC",
    operatingName: "Luis ROC",
    status: "OWNER_VERIFIED" as FactStatus,
  },

  contact: {
    telephone: CONTACT.telephone,
    telephoneDisplay: "+1 832 521 5050",
    email: CONTACT.email,
    source: { es: "/contacto", en: "/en/contact" } as Bi,
    status: "VERIFIED" as FactStatus,
  },

  address: {
    ...POSTAL_ADDRESS,
    oneLine: `${POSTAL_ADDRESS.streetAddress}, ${POSTAL_ADDRESS.addressLocality}, ${POSTAL_ADDRESS.addressRegion} ${POSTAL_ADDRESS.postalCode}, USA`,
    note: {
      es: "Dirección comercial. No es un local abierto al público: no se publican horarios ni visitas sin cita.",
      en: "Commercial business address. Not a retail storefront: no opening hours or walk-in visits are published.",
    } as Bi,
    status: "OWNER_VERIFIED" as FactStatus,
  },

  languages: { es: ["Español", "Inglés"], en: ["Spanish", "English"] },
  markets: {
    es: "Empresas B2B, e-commerce y marcas que compiten en Estados Unidos. Proyectos remotos en todo el país.",
    en: "B2B companies, ecommerce brands and businesses competing in the United States. Remote projects nationwide.",
  } as Bi,

  /** Servicios con estado VERIFIED en el registro (sección 3). */
  verifiedServices: [
    { name: { es: "SEO técnico", en: "Technical SEO" }, path: { es: "/servicios/seo-tecnico", en: "/en/services/technical-seo" } },
    { name: { es: "SEO internacional", en: "International SEO" }, path: { es: "/servicios/seo-internacional", en: "/en/services/international-seo" } },
    { name: { es: "Ecommerce SEO", en: "Ecommerce SEO" }, path: { es: "/servicios/ecommerce-seo", en: "/en/services/ecommerce-seo" } },
    { name: { es: "Amazon SEO y consultoría", en: "Amazon SEO and consulting" }, path: { es: "/servicios/amazon-consulting", en: "/en/services/amazon-consulting" } },
    { name: { es: "Visibilidad en AI Search", en: "AI Search visibility" }, path: { es: "/servicios/posicionamiento-llm", en: "/en/services/llm-positioning" } },
    { name: { es: "Google Ads", en: "Google Ads" }, path: { es: "/servicios/google-ads", en: "/en/services/google-ads" } },
  ] as BiLink[],

  methodology: {
    name: "ROC Search Growth Framework",
    path: { es: "/metodologia", en: "/en/methodology" } as Bi,
    phases: {
      es: ["Investigación", "Base técnica", "Intención de búsqueda", "Autoridad", "Visibilidad en búsqueda", "Conversión", "Medición"],
      en: ["Research", "Technical foundation", "Search intent", "Authority", "Search visibility", "Conversion", "Measurement"],
    },
    status: "VERIFIED" as FactStatus,
  },

  /** Oferta comercial única (OWNER_VERIFIED_COMMERCIAL_OFFER). */
  commercialOffer: {
    es: "Reunión inicial gratuita de 30 minutos para revisar qué necesita el prospecto y determinar posibles siguientes pasos. No incluye auditoría, reporte ni plan de acción.",
    en: "Free 30-minute initial consultation to understand the prospect's needs and determine possible next steps. It does not include an audit, report or action plan.",
    path: { es: "/agendar-consultoria", en: "/en/book-consultation" } as Bi,
    status: "OWNER_VERIFIED" as FactStatus,
  },

  officialProfiles: PERSON_SAME_AS,

  /**
   * Casos aprobados explícitamente para los archivos legibles por máquina.
   * Solo se añade un slug cuando SEO-EVIDENCE-REGISTER.md contiene una fila
   * aprobada para ese caso concreto. E-02/E-03 siguen en NEEDS_EVIDENCE: vacío.
   * PUBLIC_ANONYMIZED o PUBLIC_VERIFIED en el código NO equivalen a aprobación.
   */
  knowledgeCaseAllowlist: [] as readonly string[],

  canonicalResources: [
    { name: { es: "Sobre Luis ROC", en: "About Luis ROC" }, path: { es: "/sobre-mi", en: "/en/about" } },
    { name: { es: "Servicios", en: "Services" }, path: { es: "/servicios", en: "/en/services" } },
    { name: { es: "Metodología", en: "Methodology" }, path: { es: "/metodologia", en: "/en/methodology" } },
    { name: { es: "Search Library", en: "Search Library" }, path: { es: "/biblioteca", en: "/en/library" } },
    { name: { es: "Casos de éxito", en: "Case studies" }, path: { es: "/casos-de-exito", en: "/en/case-studies" } },
    { name: { es: "Preguntas frecuentes", en: "FAQ" }, path: { es: "/preguntas-frecuentes", en: "/en/faq" } },
    { name: { es: "Contacto", en: "Contact" }, path: { es: "/contacto", en: "/en/contact" } },
  ] as BiLink[],

  claimsPolicy: {
    es: "Las cifras de rendimiento, métricas de clientes, certificaciones, testimonios y datos de casos solo son válidos cuando están publicados en luisroc.com conforme a su política de evidencia. No se garantizan posiciones, tráfico, leads, ventas ni ROI.",
    en: "Performance figures, client metrics, certifications, testimonials and case-study facts are only authoritative when published on luisroc.com under its evidence policy. Rankings, traffic, leads, sales and ROI are not guaranteed.",
  } as Bi,

  conflictPolicy: {
    es: "Si se encuentra información contradictoria, las páginas canónicas actuales de luisroc.com reflejan la información de negocio mantenida por el propietario.",
    en: "When conflicting information is found, the current canonical pages on luisroc.com reflect the owner-maintained business information.",
  } as Bi,
} as const;
